import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
    addMessage,
    updateMessage,
    initialiseMessageThread,
    setIsLoading,
    setIsLoadingChatHistory,
    setError,
    setGateOutcome,
    setConversationId,
    resetGlobalChat,
    resetGateState,
    setConversations,
    setLoadingConversations,
    setSelectedSources,
    setAskBack,
    setReasoning,
    resetReasoning,
    setCurrentCompany,
    setIsClearingChat,
    setHasMoreData,
} from './actions';
import { getConversationHistory, getUserConversations, streamGlobalAskAicaChat, GateAction, GateReply } from './apiHelper';
import { GLOBAL_HISTORY_PAGE_SIZE } from './constants';
import { Candidate, GlobalMessage, GATE_OUTCOME_TYPE } from './types';
import { clearMemory } from '../dashboard/askAica/apiHelper';

// Same short-window turn history the leads chat sends (see askAica/useAskAica.ts) —
// Athena carries the rest of the context server-side via conversation_id.
const HISTORY_TURNS = 3;

export const useGlobalAskAica = () => {
    const dispatch = useAppDispatch();
    const userId = useAppSelector((state) => state.user.userData.userId);
    // The caller's own PNO — used ONLY as auth/ownership scoping on the history
    // lookup below (Athena requires it as a query param). This is NOT the
    // resolved-chat-turn company and must never be threaded into runTurn/orgId.
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const {
        messages,
        isLoading,
        isLoadingChatHistory,
        error,
        currentCompany,
        companyLess,
        askBack,
        decline,
        conversationId,
        conversations,
        isLoadingConversations,
        selectedSources,
        reasoning,
        isClearingChat,
        hasMoreData,
    } = useAppSelector((state) => state.globalAskAica);

    const abortStreamRef = useRef<(() => void) | null>(null);

    // Mirrors leads' streamStartedCallbackRef (useAskAica.ts) — lets the shared
    // LeftPanel know when real answer content has started streaming, so it can
    // hide the transient inline "Thinking…" block (RECUR-27369 F-19).
    const streamStartedCallbackRef = useRef<((v: boolean) => void) | null>(null);

    // Abort any in-flight stream on unmount (F-4) — otherwise it keeps running
    // and can dispatch a late chunk/completion after the portfolio surface (a
    // single-instance mount) has gone away.
    useEffect(() => {
        return () => {
            abortStreamRef.current?.();
            abortStreamRef.current = null;
        };
    }, []);

    // F-13 (lost-question race): openChat fires an async conversation-list fetch on
    // modal-open; its onSuccess auto-resumes the most-recent conversation. But the
    // guard there closes over the conversationId/messages/isLoading captured when
    // openChat was BUILT — if the user submits a turn before the list returns, that
    // stale closure still sees "no active chat" and loadConversation clobbers the
    // just-typed question with a prior conversation. This ref always holds the LIVE
    // values (assigned every render, never stale), so the resume guard reads truth.
    const liveStateRef = useRef({ conversationId, messagesLen: messages.length, isLoading });
    // Synced in useLayoutEffect (commit phase), never during render — React can
    // replay or discard a render, and a ref write during render would let
    // openChat's async guard read a value from a render that never committed.
    useLayoutEffect(() => {
        liveStateRef.current = { conversationId, messagesLen: messages.length, isLoading };
    }, [conversationId, messages.length, isLoading]);

    // Monotonic token guarding loadConversation's async history fetch — bumped by
    // runTurn/newChat/loadConversation itself so a SUPERSEDED history response
    // (the user started a turn, reset the chat, or picked another conversation
    // before the fetch returned) is discarded instead of clobbering newer state.
    const historyLoadTokenRef = useRef(0);

    const fetchUserConversations = useCallback(() => {
        if (!userId) return;
        dispatch(setLoadingConversations(true));
        getUserConversations(userId, {
            onSuccess: (data) => dispatch(setConversations(data?.conversations ?? [])),
            // 401 (no principal) or any other failure → empty sidebar, never a crash.
            onError: () => dispatch(setConversations([])),
        });
    }, [dispatch, userId]);

    const runTurn = useCallback(
        (options: { displayContent?: string; apiContent?: string; gateReply?: GateReply; gateAction?: GateAction }) => {
            if (isLoading) return;

            console.info('[AskAica:portfolio] turn start', {
                conversationId,
                sources: selectedSources,
                hasApiContent: !!options.apiContent,
                displayContentLength: options.displayContent?.length ?? 0,
                gateAction: options.gateAction ? Object.keys(options.gateAction)[0] : undefined,
                gateReply: options.gateReply ? Object.keys(options.gateReply)[0] : undefined,
            });

            // A new turn supersedes any pending history load (e.g. resume-on-open
            // still in flight when the user starts typing) — bump the token so
            // that load's result is discarded rather than overwriting this turn.
            historyLoadTokenRef.current += 1;

            // A previous turn's stream may still be in flight (e.g. rapid
            // send/switch/clear) — abort it before starting this one so its late
            // chunks (e.g. gate_outcome) can't race and re-assert a stale company.
            abortStreamRef.current?.();
            abortStreamRef.current = null;

            if (options.displayContent) {
                dispatch(addMessage({ id: Date.now(), type: 'user', content: options.displayContent, timestamp: new Date() }));
            }

            const aiMessageId = Date.now() + 1;
            dispatch(addMessage({ id: aiMessageId, type: 'assistant', content: '', timestamp: new Date() }));
            dispatch(setIsLoading(true));
            dispatch(setError(null));
            dispatch(resetReasoning());
            streamStartedCallbackRef.current?.(false);

            const historyTurns = messages.slice(-HISTORY_TURNS).map((msg) => ({
                role: msg.type === 'user' ? 'user' : 'assistant',
                content: msg.content,
            }));
            // gate_action-only turns (switch/clear) don't add a new utterance —
            // they replay the existing transcript with the gate delta attached.
            const apiMessages = options.apiContent
                ? [...historyTurns, { role: 'user', content: options.apiContent }].slice(-HISTORY_TURNS)
                : historyTurns;

            try {
                abortStreamRef.current = streamGlobalAskAicaChat(
                    {
                        messages: apiMessages,
                        sources: selectedSources,
                        userId,
                        // INVARIANT: the global surface NEVER sends body org_id / the
                        // x-organization-id header, on ANY turn — including follow-ups
                        // after a company has already been resolved. Pinning the org here
                        // would let Athena treat the turn as company-scoped and skip the
                        // gate, which is exactly what breaks in-thread switching (naming a
                        // new company mid-conversation no longer triggers ask_back/switch).
                        // The gate alone drives which company is active each turn via its
                        // resolved/continue/switch/ask_back outcome; continuity comes from
                        // conversation_id, not from the FE echoing currentCompany.org_id.
                        orgId: null,
                        conversationId,
                        gateReply: options.gateReply,
                        gateAction: options.gateAction,
                    },
                    (content, gateOutcome, chunkConversationId, reasoning, hasNewReasoning, sources) => {
                        // ask_back's raw prompt ("I found multiple companies matching…") is
                        // never persisted as message content — the docked CompanyPicker IS the
                        // whole response for this turn (see AskAicaInputBox's portfolio-only
                        // picker). Skipping it here means there is nothing to suppress at
                        // render time, and the message stays empty even once it's no longer
                        // the last one in the thread.
                        if (gateOutcome?.type !== GATE_OUTCOME_TYPE.ASK_BACK) dispatch(updateMessage(aiMessageId, content, sources));
                        // Real answer content (not an ask_back turn, which keeps the picker up
                        // instead of a streamed bubble) — flip the signal so LeftPanel hides the
                        // transient inline "Thinking…" block (leads parity, see useAskAica.ts:229).
                        if (gateOutcome?.type !== GATE_OUTCOME_TYPE.ASK_BACK && content) streamStartedCallbackRef.current?.(true);
                        if (chunkConversationId) dispatch(setConversationId(chunkConversationId));
                        if (gateOutcome) dispatch(setGateOutcome(gateOutcome));
                        dispatch(setReasoning(reasoning, hasNewReasoning));
                    },
                    (response) => {
                        console.info('[AskAica:portfolio] turn end', {
                            conversationId: response.conversationId ?? conversationId,
                            status: 'success',
                        });
                        dispatch(setIsLoading(false));
                        if (response.content && response.gateOutcome?.type !== GATE_OUTCOME_TYPE.ASK_BACK) {
                            dispatch(updateMessage(aiMessageId, response.content, response.sources));
                        }
                        if (response.conversationId) dispatch(setConversationId(response.conversationId));
                        if (response.gateOutcome) dispatch(setGateOutcome(response.gateOutcome));
                        // Each SSE frame already dispatched its own reasoning delta via onChunk
                        // above — re-dispatching the accumulated response.reasoning here would
                        // append it a second time (setReasoning APPENDS, see reducer.ts).
                        // Refresh the sidebar so a new conversation appears without waiting for a poll.
                        fetchUserConversations();
                    },
                    (err) => {
                        // Enriched with backend correlation (conversationId/status/response
                        // snippet, when available) so a failed turn can be tied back to
                        // Athena's logs — err is a plain Error here (see apiHelper.ts), not
                        // an AxiosError, so no header/token to redact.
                        console.error('Error in streamGlobalAskAicaChat:', {
                            conversationId,
                            status: err?.status,
                            responseSnippet: err?.responseSnippet,
                            message: err?.message,
                        });
                        dispatch(setIsLoading(false));
                        dispatch(setError(err?.message || 'Failed to get response'));
                        // No conversationId to re-dispatch here: onChunk above already
                        // dispatches setConversationId as soon as any chunk carries one,
                        // independent of this turn's outcome — a mid-stream error can't
                        // lose an id that already landed in Redux (F-18).
                    },
                );
            } catch (err: any) {
                dispatch(setIsLoading(false));
                dispatch(setError(err?.message || 'Failed to send message'));
            }

            return () => abortStreamRef.current?.();
        },
        [dispatch, isLoading, messages, selectedSources, userId, currentCompany, conversationId, fetchUserConversations],
    );

    /** A normal typed message — also how an ask_back free-text reply ("2", "yes",
     * a name/identifier) is sent: the gate interprets it, no gate_reply attached.
     * Returns runTurn's abort cleanup so the shared AskAicaInputBox's Stop-chat
     * button works on the portfolio surface too (RECUR-27369). */
    const sendMessage = useCallback(
        (content: string) => {
            // Leads-parity send-guard (F-18): blocks while a turn is already in
            // flight, so a message typed before the previous turn's
            // conversation_id has arrived can't fire a second /chat call with no
            // conversation_id and fragment into a brand-new conversation.
            if (!content.trim() || isLoading) return undefined;
            return runTurn({ displayContent: content, apiContent: content });
        },
        [runTurn, isLoading],
    );

    /** Tapping a candidate row on the picker. */
    const replyToGate = useCallback(
        (candidate: Candidate) => {
            runTurn({ displayContent: candidate.name, apiContent: candidate.name, gateReply: { selected_org_id: candidate.org_id } });
        },
        [runTurn],
    );

    /** Picker's "✕" — dismisses the open ask_back picker without picking a candidate. */
    const dismissAskBack = useCallback(() => dispatch(setAskBack(null)), [dispatch]);

    /** Chip "✕" — clears the resolved company, back to company-less.
     * v1 has no explicit switcher affordance: switching companies is done by simply
     * naming a new one in the next message, which the gate resolves on its own
     * (GateAction.switch stays available at the apiHelper/contract level for a
     * future explicit-switcher UI, but no hook method exposes it today). */
    const clearCompany = useCallback(() => {
        runTurn({ gateAction: { clear: true } });
    }, [runTurn]);

    /** New Chat — drops the held conversation_id + gate/message state. */
    const newChat = useCallback(() => {
        // Resetting supersedes any pending history load — discard its result.
        historyLoadTokenRef.current += 1;
        // Abort any in-flight stream from the abandoned conversation — otherwise
        // it keeps running and can re-assert a company/gate state after reset.
        abortStreamRef.current?.();
        abortStreamRef.current = null;
        dispatch(resetGlobalChat());
    }, [dispatch]);

    /** "Delete chat" — clears the current conversation's memory on Athena, keyed
     * on conversation_id (RECUR-27369 parity with the leads clearChatMemory).
     * No-op when there's nothing to clear (no active conversation). Isolated to
     * the global slice — never touches the leads askAica reducer. Sets a real
     * isClearingChat flag and aborts any in-flight stream first (F-7 parity with
     * leads' setIsClearingChat). org_id is omitted (not defaulted to '') once a
     * conversation_id is present, per the Athena contract (F-8). */
    const clearChat = useCallback(() => {
        if (!conversationId) return;
        abortStreamRef.current?.();
        abortStreamRef.current = null;
        dispatch(setIsClearingChat(true));
        clearMemory(userId, currentCompany?.org_id, conversationId)
            .then(() => {
                toast.success('Chat memory cleared successfully');
                newChat();
                fetchUserConversations();
            })
            .catch((err) => {
                // Never log the raw error — an AxiosError's `config.headers` carries the
                // session cerberus token (see useAskAicaFileActions.ts::openFile).
                console.error('Error clearing chat:', err instanceof Error ? err.message : 'unknown error');
                toast.error('Failed to clear chat memory');
                fetchUserConversations();
            })
            .finally(() => dispatch(setIsClearingChat(false)));
    }, [conversationId, userId, currentCompany, newChat, fetchUserConversations, dispatch]);

    /** Loads a conversation's message pairs — today, only ever the user's single
     * active portfolio conversation (single-conversation-per-user model,
     * 2026-08-10), called from openChat's resume-on-open. No user-facing
     * switcher exists, so there's no "conversation A already streaming, switch
     * to B" case to guard against here (see the unmount-cleanup abort in the
     * effect above for the general "surface goes away mid-stream" case).
     * The history response carries the conversation's last resolved company
     * (current_company: {org_id, name} | null — Athena BE follow-up for F-6), so the
     * "Answering for X" chip IS restored on reload (see the setCurrentCompany dispatch
     * below); a company-less conversation returns null and the chip stays clear.
     * Resets gate state first (currentCompany/companyLess/askBack/decline, same reset
     * newChat performs) so a stale chip can never leak into the next turn — the chip
     * is re-established solely from this conversation's current_company. */
    const loadConversation = useCallback(
        (convId: string) => {
            // Bumping first invalidates any still-pending PRIOR load (rapid
            // double-open, or a switch mid-fetch) — its .then/.catch below will
            // see a stale token and discard its result instead of overwriting
            // whatever this call (or a later turn/reset) produces.
            const token = ++historyLoadTokenRef.current;
            dispatch(setIsLoadingChatHistory(true));
            dispatch(resetGateState());
            getConversationHistory(convId, userId, investorOrgId)
                .then((data) => {
                    // Superseded by a new turn, newChat, or another loadConversation
                    // call while this fetch was in flight — discard, don't clobber.
                    if (historyLoadTokenRef.current !== token) return;
                    const messageThread: GlobalMessage[] = [];
                    const items = Array.isArray(data?.items) ? data.items : [];
                    // F-9: a full page hints older messages may exist — real signal
                    // instead of a hardcoded false (the portfolio surface has no
                    // "load more" UI yet, so this is informational for now).
                    dispatch(setHasMoreData(items.length >= GLOBAL_HISTORY_PAGE_SIZE));
                    if (data && Array.isArray(data.items)) {
                        data.items.forEach((item: { user_message?: any; assistant_message?: any }) => {
                            // Company-ID gate ✕-clear marker (query_source='id_clear'): a control row the
                            // backend persists ONLY so its sticky current-company lookup knows the company
                            // was cleared — it is not a chat turn (no assistant reply) and must never render.
                            // Suppress the whole pair on reload (the DB row stays intact; this is render-only).
                            // Portfolio-only: in-lead never emits id_clear. Filtering on query_source (not on
                            // empty content) also catches legacy clear rows that carry a stale replayed body.
                            if (item.user_message?.query_source === 'id_clear') return;
                            if (item.user_message) {
                                messageThread.push({
                                    id: item.user_message.id ?? Date.now(),
                                    type: 'user',
                                    content: item.user_message.input_text ?? '',
                                    timestamp: new Date(item.user_message.created_at ?? Date.now()),
                                });
                            }
                            if (item.assistant_message) {
                                messageThread.push({
                                    id: item.assistant_message.id ?? Date.now() + 1,
                                    type: 'assistant',
                                    content: item.assistant_message.input_text ?? '',
                                    timestamp: new Date(item.assistant_message.created_at ?? Date.now()),
                                    // Athena H-11: structural gate ask-back-prompt flag — see
                                    // GlobalMessage.is_gate_control doc comment. Additive/
                                    // optional; undefined when the BE hasn't sent it.
                                    is_gate_control: item.assistant_message.is_gate_control,
                                });
                            }
                        });
                    }
                    // Order-agnostic w.r.t. server sort — chronological ascending either way.
                    messageThread.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
                    dispatch(initialiseMessageThread(messageThread));
                    dispatch(setConversationId(convId));
                    // F-6: restore the "Answering for <company>" chip. Athena's history
                    // response now carries the conversation's last resolved company
                    // (current_company: {org_id, name} | null). null → company-less
                    // conversation, chip stays clear. The resetGateState() above cleared
                    // any prior chip before this load, so this is the sole source of truth.
                    dispatch(setCurrentCompany(data.current_company ?? null));
                })
                .catch((err) => {
                    if (historyLoadTokenRef.current !== token) return;
                    // Never log the raw error — an AxiosError's `config.headers` carries the
                    // session cerberus token (see useAskAicaFileActions.ts::openFile).
                    console.error('Error fetching global conversation history:', err instanceof Error ? err.message : 'unknown error');
                    dispatch(setIsLoadingChatHistory(false));
                    dispatch(setError('Failed to load conversation'));
                });
        },
        [dispatch, userId, investorOrgId],
    );

    /** F-B (resume-on-open): called when the portfolio surface mounts. Fetches
     * the user's conversation list (GET /chat/conversations/user — under the
     * single-conversation-per-user model this is at most one row) and — mirroring
     * how the leads chat resumes history on open — loads its messages so
     * reopening/reloading doesn't start blank while Athena's memory secretly
     * continues the old thread. Skips the auto-load if a conversation is already
     * active in state (surface was merely re-toggled mid-chat). */
    const openChat = useCallback(() => {
        if (!userId) return;
        dispatch(setLoadingConversations(true));
        getUserConversations(userId, {
            onSuccess: (data) => {
                const list = data?.conversations ?? [];
                dispatch(setConversations(list));
                // Read the LIVE state, not the closure — a turn may have started
                // (or a conversation been picked) while this list request was in
                // flight. Auto-resume ONLY when the chat is still genuinely idle:
                // no active conversation, no messages, nothing streaming. Otherwise
                // loadConversation would replace the user's just-typed question with
                // the most-recent prior conversation (F-13).
                const { conversationId: liveCid, messagesLen, isLoading: liveLoading } = liveStateRef.current;
                if (!liveCid && messagesLen === 0 && !liveLoading && list.length > 0) {
                    loadConversation(list[0].conversation_id);
                }
            },
            onError: () => dispatch(setConversations([])),
        });
    }, [dispatch, userId, loadConversation]);

    const updateSelectedSources = useCallback((sources: string[]) => dispatch(setSelectedSources(sources)), [dispatch]);

    const registerStreamStartedCallback = useCallback((cb: (v: boolean) => void) => {
        streamStartedCallbackRef.current = cb;
    }, []);

    return {
        messages,
        isLoading,
        isLoadingChatHistory,
        error,
        currentCompany,
        companyLess,
        askBack,
        decline,
        conversationId,
        conversations,
        isLoadingConversations,
        selectedSources,
        sendMessage,
        replyToGate,
        dismissAskBack,
        clearCompany,
        newChat,
        clearChat,
        loadConversation,
        fetchUserConversations,
        openChat,
        reasoning,
        setSelectedSources: updateSelectedSources,
        isClearingChat,
        hasMoreData,
        registerStreamStartedCallback,
    };
};

export default useGlobalAskAica;
